import { createSlice, createSelector } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

// File upload states
export const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PAUSED: 'paused'
};

const initialState = {
  uploads: {}, // Object with file_id as key
  isUploadPanelOpen: false,
  totalActiveUploads: 0
};

const fileUploadSlice = createSlice({
  name: 'fileUpload',
  initialState,
  reducers: {
    // Initialize a new file upload
    initializeUpload: (state, action) => {
      const {
        file,
        totalChunks,
        fileId: providedFileId,
        folderId,
        uploadEndpoint,
      } = action.payload;
      const fileId = providedFileId || uuidv4();
      
      state.uploads[fileId] = {
        file_id: fileId,
        file_name: file.name,
        file_size: file.size,
        total_chunks: totalChunks,
        uploaded_chunks: 0,
        progress: 0,
        status: UPLOAD_STATUS.PENDING,
        error: null,
        startTime: Date.now(),
        endTime: null,
        retryCount: 0,
        currentChunkIndex: 0,
        folderId: folderId || null, // Store folderId if provided
        uploadEndpoint: uploadEndpoint || null, // Optional per-file upload endpoint
      };
      
      state.totalActiveUploads += 1;
      state.isUploadPanelOpen = true;
    },

    // Start uploading a file
    startUpload: (state, action) => {
      const { fileId } = action.payload;
      if (state.uploads[fileId]) {
        state.uploads[fileId].status = UPLOAD_STATUS.UPLOADING;
      }
    },

    // Update upload progress for a specific chunk
    updateChunkProgress: (state, action) => {
      const { fileId, chunkIndex } = action.payload;
      if (state.uploads[fileId]) {
        const upload = state.uploads[fileId];
        upload.uploaded_chunks = chunkIndex + 1;
        upload.currentChunkIndex = chunkIndex;
        upload.progress = Math.round((upload.uploaded_chunks / upload.total_chunks) * 100);
      }
    },

    // Mark upload as completed
    completeUpload: (state, action) => {
      const { fileId, path, url } = action.payload;
      if (state.uploads[fileId]) {
        state.uploads[fileId].status = UPLOAD_STATUS.COMPLETED;
        state.uploads[fileId].progress = 100;
        state.uploads[fileId].endTime = Date.now();
        state.uploads[fileId].path = path || url || null;
        state.uploads[fileId].uploadUrl = url || path || null;
        state.totalActiveUploads = Math.max(0, state.totalActiveUploads - 1);
      }
    },

    // Mark upload as failed
    failUpload: (state, action) => {
      const { fileId, error } = action.payload;
      if (state.uploads[fileId]) {
        state.uploads[fileId].status = UPLOAD_STATUS.FAILED;
        state.uploads[fileId].error = error;
        state.uploads[fileId].endTime = Date.now();
        state.totalActiveUploads = Math.max(0, state.totalActiveUploads - 1);
      }
    },

    // Retry failed upload
    retryUpload: (state, action) => {
      const { fileId } = action.payload;
      if (state.uploads[fileId]) {
        state.uploads[fileId].status = UPLOAD_STATUS.PENDING;
        state.uploads[fileId].error = null;
        state.uploads[fileId].retryCount += 1;
        state.uploads[fileId].currentChunkIndex = 0;
        state.uploads[fileId].uploaded_chunks = 0;
        state.uploads[fileId].progress = 0;
        state.totalActiveUploads += 1;
      }
    },

    // Pause upload
    pauseUpload: (state, action) => {
      const { fileId } = action.payload;
      if (state.uploads[fileId]) {
        state.uploads[fileId].status = UPLOAD_STATUS.PAUSED;
        state.totalActiveUploads = Math.max(0, state.totalActiveUploads - 1);
      }
    },

    // Resume paused upload
    resumeUpload: (state, action) => {
      const { fileId } = action.payload;
      if (state.uploads[fileId]) {
        state.uploads[fileId].status = UPLOAD_STATUS.PENDING;
        state.totalActiveUploads += 1;
      }
    },

    // Remove upload from list (user manually closes)
    removeUpload: (state, action) => {
      const { fileId } = action.payload;
      if (state.uploads[fileId]) {
        const wasActive = [UPLOAD_STATUS.PENDING, UPLOAD_STATUS.UPLOADING].includes(
          state.uploads[fileId].status
        );
        if (wasActive) {
          state.totalActiveUploads = Math.max(0, state.totalActiveUploads - 1);
        }
        delete state.uploads[fileId];
      }
    },

    // Toggle upload panel visibility
    toggleUploadPanel: (state) => {
      state.isUploadPanelOpen = !state.isUploadPanelOpen;
    },

    // Close upload panel
    closeUploadPanel: (state) => {
      state.isUploadPanelOpen = false;
    },

    // Clear all completed uploads
    clearCompletedUploads: (state) => {
      Object.keys(state.uploads).forEach(fileId => {
        if (state.uploads[fileId].status === UPLOAD_STATUS.COMPLETED) {
          delete state.uploads[fileId];
        }
      });
    },

    // Clear all uploads (for cleanup)
    clearAllUploads: (state) => {
      state.uploads = {};
      state.totalActiveUploads = 0;
      state.isUploadPanelOpen = false;
    }
  }
});

export const {
  initializeUpload,
  startUpload,
  updateChunkProgress,
  completeUpload,
  failUpload,
  retryUpload,
  pauseUpload,
  resumeUpload,
  removeUpload,
  toggleUploadPanel,
  closeUploadPanel,
  clearCompletedUploads,
  clearAllUploads
} = fileUploadSlice.actions;

// Selectors
export const selectAllUploads = (state) => state.fileUpload.uploads;
export const selectUploadById = (fileId) => (state) => state.fileUpload.uploads[fileId];
export const selectIsUploadPanelOpen = (state) => state.fileUpload.isUploadPanelOpen;
export const selectTotalActiveUploads = (state) => state.fileUpload.totalActiveUploads;

// Memoized selectors to prevent unnecessary re-renders
export const selectActiveUploads = createSelector(
  [selectAllUploads],
  (uploads) => Object.values(uploads).filter(upload => 
    [UPLOAD_STATUS.PENDING, UPLOAD_STATUS.UPLOADING].includes(upload.status)
  )
);

export const selectCompletedUploads = createSelector(
  [selectAllUploads],
  (uploads) => Object.values(uploads).filter(upload => 
    upload.status === UPLOAD_STATUS.COMPLETED
  )
);

export default fileUploadSlice.reducer;
